import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useDialog } from "@/context/DialogProvider";
import { TrendingUp, AlertTriangle, Wrench, Building2, Gauge, Calendar, MapPin, ChevronRight, Users as UsersIcon, Users2, ShieldAlert, ClipboardList, ClipboardCheck, History, Siren, HardDrive, Settings, Activity } from "lucide-react";
import { Link } from "react-router-dom";

function customerStatus(t) {
  if (t.is_active === false) return { label: "Discontinued", cls: "err" };
  if (!t.tier_name) return { label: "No plan", cls: "warn" };
  if (t.trial_expires_at) {
    const ms = new Date(t.trial_expires_at).getTime() - Date.now();
    if (ms < 0) return { label: "Trial expired", cls: "err" };
    const days = Math.ceil(ms / 86400000);
    if (days <= 7) return { label: `${t.tier_name} — ${days}d left`, cls: "warn" };
  }
  return { label: t.tier_name, cls: "ok" };
}

// Human-readable byte formatter. Auto-scales between B / KB / MB / GB
// depending on magnitude and drops trailing zeros so tiny per-tenant
// contributions don't look artificially inflated in the table.
function formatBytes(bytes) {
  if (!bytes || bytes < 1) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(decimals)} ${units[i]}`;
}

export default function Dashboard() {
  const { user, hiddenModules } = useAuth();
  const { isAllSites, activeSite, activeSiteId, setActiveSite, visibleSites, hasNoSiteAssigned } = useSite();
  const { prompt, notify } = useDialog();
  const [stats, setStats] = useState(null);
  const [siteCards, setSiteCards] = useState(null);
  const [recent, setRecent] = useState([]);
  const [fsmsStatus, setFsmsStatus] = useState([]);
  const [foodPrep, setFoodPrep] = useState(null);
  const [storage, setStorage] = useState(null);
  const [err, setErr] = useState("");

  const isSuperAdmin = user?.role === "super_admin";
  const isStaff = user?.role === "staff";
  const showSiteOverview = !isSuperAdmin && !isStaff && isAllSites && visibleSites.length > 1;

  useEffect(() => {
    if (isSuperAdmin) return;
    api.get("/food-prep/summary")
      .then(({ data }) => setFoodPrep(data))
      .catch(() => setFoodPrep(null));
  }, [isSuperAdmin, activeSiteId]);

  useEffect(() => {
    (async () => {
      try {
        if (isSuperAdmin) {
          const [t, u, sg] = await Promise.all([
            api.get("/admin/tenants"),
            api.get("/users"),
            api.get("/admin/storage/usage").catch(() => ({ data: null })),
          ]);
          setStats({
            super_admin: true,
            tenants: t.data.tenants || [],
            users: u.data.users || [],
          });
          setStorage(sg.data);
          return;
        }
        if (hasNoSiteAssigned) { setStats({ blocked: true }); return; }
        if (isStaff) {
          // Staff don't have access to /stats/dashboard or /fsms-team/status
          // (manager-and-above scopes). Render a slim staff view using only
          // the data they can fetch — their own recent readings.
          const r = await api.get("/readings?days=7");
          setStats({ staff: true });
          setRecent((r.data.readings || []).slice(0, 8));
          return;
        }
        const requests = [
          api.get("/stats/dashboard"),
          api.get("/readings?days=7"),
          api.get("/fsms-team/status").catch(() => ({ data: { sites: [] } })),
        ];
        if (showSiteOverview) requests.push(api.get("/stats/sites-summary"));
        const [s, r, fs, ss] = await Promise.all(requests);
        setStats(s.data);
        setRecent((r.data.readings || []).slice(0, 8));
        setFsmsStatus(fs.data?.sites || []);
        setSiteCards(ss?.data?.sites || null);
      } catch (e) {
        setErr("Could not load dashboard");
      }
    })();
  }, [isSuperAdmin, isStaff, activeSiteId, showSiteOverview, hasNoSiteAssigned]);

  if (err) return <div className="vb-pill err" data-testid="dashboard-error">{err}</div>;
  if (!stats) return <div data-testid="dashboard-loading">Loading…</div>;

  if (stats.blocked) {
    return (
      <div className="vb-fade-in flex flex-col items-center justify-center text-center" style={{ minHeight: "60vh" }} data-testid="no-site-blocked">
        <div className="vb-card p-8 max-w-md">
          <div className="text-4xl mb-3">🔒</div>
          <h1 className="vb-heading text-xl mb-2" style={{ color: "var(--vb-text-2)" }}>No site assigned</h1>
          <p className="opacity-80">You haven't been assigned to any site yet. Ask your manager or admin to assign you to a site so you can log readings and view equipment.</p>
        </div>
      </div>
    );
  }

  // --- Staff view (slim) ---
  if (stats.staff) {
    const quickTiles = [
      { to: "/app/log",            label: "Log a reading",       icon: ClipboardList,  desc: "Submit a new equipment reading" },
      { to: "/app/daily-tracking", label: "Daily Tracking",      icon: ClipboardCheck, desc: "Hygiene & cleanliness checks" },
      { to: "/app/my-readings",    label: "My Readings",         icon: History,        desc: "Your reading history" },
      { to: "/app/emergency",      label: "Incident Response",   icon: Siren,          desc: "Report or review incidents", module: "emergency" },
    ].filter((t) => !t.module || !(hiddenModules || []).includes(t.module));
    return (
      <div className="vb-fade-in" data-testid="dashboard-staff">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>
              Hello, {user?.name?.split(" ")[0] || "team"}
            </h1>
            <p className="opacity-70">
              {activeSite?.name ? `Logged in for ${activeSite.name}.` : "Welcome back."}
              {" "}Use the shortcuts below to log a reading, run your daily tracking, or report an incident.
            </p>
          </div>
          <Link to="/app/log" className="vb-btn" data-testid="quick-log">+ Log reading</Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {quickTiles.map((t) => (
            <Link key={t.to} to={t.to} className="vb-card p-4 hover:shadow-md transition-shadow" data-testid={`staff-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <t.icon size={20} style={{ color: "var(--vb-text-2)" }} />
              <div className="vb-heading text-base mt-2" style={{ color: "var(--vb-text-2)" }}>{t.label}</div>
              <p className="text-xs opacity-70 mt-1">{t.desc}</p>
            </Link>
          ))}
        </div>

        <div className="vb-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={16} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Your recent readings</h2>
            <span className="text-xs opacity-60">last 7 days · {recent.length}</span>
          </div>
          {recent.length === 0 ? (
            <p className="text-sm opacity-60 m-0" data-testid="staff-no-readings">
              No readings logged yet. Tap <strong>Log a reading</strong> to record one.
            </p>
          ) : (
            <ul className="text-sm space-y-2 m-0 p-0 list-none" data-testid="staff-recent-list">
              {recent.map((r) => (
                <li key={r.id} className="flex items-center gap-3 flex-wrap">
                  <span className="opacity-70 text-xs" style={{ minWidth: 110 }}>
                    {new Date(r.created_at || r.logged_at).toLocaleString()}
                  </span>
                  <span className="font-medium">{r.ccp_name || r.equipment_name || "Reading"}</span>
                  <span className="opacity-80">{r.value}{r.unit ? ` ${r.unit}` : ""}</span>
                  {r.is_deviation && (
                    <span className="vb-pill err text-[10px]"><AlertTriangle size={9} /> Deviation</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // --- Super admin view ---
  if (isSuperAdmin) {
    const tenants = (stats.tenants || []).slice().sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const activeCount = tenants.filter(t => t.is_active !== false).length;
    const totalUsers = stats.users.length;
    const tenantUsers = stats.users.filter(u => u.tenant_id);
    const ownersCount = tenantUsers.filter(u => u.role === "owner").length;
    const opsCount = tenantUsers.filter(u => ["manager", "supervisor", "staff"].includes(u.role)).length;

    // Per-tenant storage lookup so the customer row can render each
    // tenant's contribution without an extra API call per row.
    const storageBytesByTenant = {};
    for (const row of storage?.per_tenant || []) {
      storageBytesByTenant[row.tenant_id] = row.bytes;
    }
    const usedPct = storage?.used_pct ?? 0;
    const usageBarColor =
      usedPct >= 90 ? "var(--vb-error)"
      : usedPct >= 75 ? "var(--vb-warning, #f97316)"
      : "var(--vb-accent)";

    const openStorageSettings = async () => {
      const current = storage?.limit_gb ?? 5;
      const next = await prompt({
        title: "Storage limit",
        message: `Set the maximum object-store cap for this platform. Current: ${current} GB. Enter the new cap in gigabytes (e.g. 5, 10, 25).`,
        defaultValue: String(current),
        inputType: "number",
        placeholder: "GB",
        confirmLabel: "Save cap",
      });
      if (next === null) return;
      const gb = parseFloat(next);
      if (!isFinite(gb) || gb <= 0) {
        await notify({ title: "Invalid limit", message: "Enter a positive number of gigabytes.", variant: "err" });
        return;
      }
      try {
        await api.put("/admin/storage/limit", { limit_gb: gb });
        const { data } = await api.get("/admin/storage/usage?fresh=true");
        setStorage(data);
        await notify({ title: "Storage cap updated", message: `New limit: ${gb} GB.`, variant: "ok" });
      } catch (e) {
        await notify({
          title: "Couldn't update cap",
          message: e?.response?.data?.detail || "Something went wrong.",
          variant: "err",
        });
      }
    };

    return (
      <div className="vb-fade-in" data-testid="superadmin-dashboard">
        <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>Vanguard Beacon — Platform overview</h1>
        <p className="opacity-70 mb-6">Welcome, {user.name}. You have a platform-wide view of all customers and users.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="vb-stat" data-testid="stat-tenants"><span className="label">Active customers</span><span className="value" style={{ color: "var(--vb-text-2)" }}>{activeCount}</span></div>
          <div className="vb-stat" data-testid="stat-total-users"><span className="label">Total users</span><span className="value" style={{ color: "var(--vb-text-2)" }}>{totalUsers}</span></div>
          <div className="vb-stat"><span className="label">Owners</span><span className="value" style={{ color: "var(--vb-accent)" }}>{ownersCount}</span></div>
          <div className="vb-stat"><span className="label">Managers + Staff</span><span className="value" style={{ color: "var(--vb-text-2)" }}>{opsCount}</span></div>
        </div>

        {/* Storage tile — full-width card with usage bar + gear settings */}
        <div className="vb-card p-5 mb-8 relative" data-testid="storage-tile">
          <button
            onClick={openStorageSettings}
            className="absolute top-3 right-3 p-1.5 rounded-md hover:opacity-70 transition-opacity"
            style={{ color: "var(--vb-text-2)", zIndex: 2 }}
            title="Set storage cap"
            data-testid="storage-settings-gear"
          >
            <Settings size={16} />
          </button>
          <div className="flex items-start gap-4">
            <div
              className="rounded-full p-3 shrink-0"
              style={{ background: "var(--vb-bg-2)", color: "var(--vb-text-2)" }}
            >
              <HardDrive size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider opacity-70 mb-0.5">
                Platform storage used
              </div>
              {storage ? (
                <>
                  <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                    <div
                      className="text-2xl font-semibold"
                      style={{ color: "var(--vb-text-2)" }}
                      data-testid="storage-used-label"
                    >
                      {formatBytes(storage.total_bytes)}
                    </div>
                    <div className="text-sm opacity-70">
                      of {storage.limit_gb} GB
                      <span className="mx-2 opacity-40">·</span>
                      {storage.total_objects} files
                      <span className="mx-2 opacity-40">·</span>
                      <span
                        style={{ color: usageBarColor, fontWeight: 600 }}
                        data-testid="storage-used-pct"
                      >
                        {storage.used_pct}%
                      </span>
                    </div>
                  </div>
                  <div
                    className="rounded-full overflow-hidden"
                    style={{ height: 8, background: "var(--vb-bg-2)" }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, usedPct)}%`,
                        height: "100%",
                        background: usageBarColor,
                        transition: "width 0.3s ease",
                      }}
                      data-testid="storage-usage-bar"
                    />
                  </div>
                </>
              ) : (
                <div className="opacity-60 text-sm">Loading storage usage…</div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-end mb-3">
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>Customers</h2>
          <Link to="/app/tenants" className="vb-link text-sm" data-testid="dash-manage-tenants">Manage customers →</Link>
        </div>
        <div className="vb-card overflow-hidden">
          <table className="vb-table">
            <thead><tr><th>Entity</th><th>Users</th><th>Storage</th><th>Joined</th><th>Subscription</th></tr></thead>
            <tbody>
              {tenants.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No customers onboarded yet.</td></tr>
              )}
              {tenants.map(t => {
                const status = customerStatus(t);
                const tenantBytes = storageBytesByTenant[t.id];
                return (
                  <tr key={t.id} data-testid={`customer-row-${t.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="opacity-60" />
                        <strong>{t.company_name}</strong>
                      </div>
                    </td>
                    <td>{t.user_count}</td>
                    <td data-testid={`customer-storage-${t.id}`}>
                      {tenantBytes == null ? (
                        <span className="opacity-40">—</span>
                      ) : (
                        <span
                          className="tabular-nums"
                          title={`${tenantBytes.toLocaleString()} bytes`}
                        >
                          {formatBytes(tenantBytes)}
                        </span>
                      )}
                    </td>
                    <td className="opacity-70">{new Date(t.created_at).toLocaleDateString()}</td>
                    <td><span className={`vb-pill ${status.cls}`}>{status.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const cards = [
    { label: "Active incidents", value: stats.active_incidents ?? 0, icon: Siren, color: (stats.active_incidents ?? 0) > 0 ? "var(--vb-error)" : "var(--vb-accent)" },
    { label: "Open deviations", value: stats.open_deviations, icon: AlertTriangle, color: "var(--vb-error)" },
    { label: "Deviations (7d)", value: stats.deviations_7d, icon: TrendingUp, color: "var(--vb-warning)" },
    {
      label: "Sign-off backlog (30d)",
      value: stats.signoff_backlog_days ?? 0,
      icon: ShieldAlert,
      color: (stats.signoff_backlog_days ?? 0) > 0 ? "var(--vb-warning)" : "var(--vb-accent)",
      sub: (stats.signoff_backlog_days ?? 0) > 0
        ? `across ${stats.signoff_backlog_sites} site${stats.signoff_backlog_sites === 1 ? "" : "s"}`
        : "all caught up",
      to: "/app/ccps",
      testid: "tile-signoff-backlog",
    },
    { label: "Sites", value: stats.total_sites, icon: Building2, color: "var(--vb-text-2)" },
    { label: "Equipment", value: stats.total_equipment, icon: Wrench, color: "var(--vb-text-2)" },
    { label: "CCPs", value: stats.total_ccps, icon: Gauge, color: "var(--vb-text-2)" },
    { label: "Calibration due (30d)", value: stats.calibration_due_30d, icon: Calendar, color: "var(--vb-warning)" },
  ];

  return (
    <div className="vb-fade-in" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>Operational overview</h1>
          <p className="opacity-70">
            {isAllSites
              ? `Live HACCP & ISO 22000 compliance signals across ${visibleSites.length || "all"} site${visibleSites.length === 1 ? "" : "s"}.`
              : `Site: ${activeSite?.name || ""} — drill down or switch to All sites in the top bar.`}
          </p>
        </div>
        <Link to="/app/log" className="vb-btn" data-testid="quick-log">+ Log reading</Link>
      </div>

      {/* FSMS vacancy banner — shown only when at least one visible site has open vacancies */}
      {(() => {
        const noncompliant = fsmsStatus.filter((s) => !s.compliant);
        if (noncompliant.length === 0) return null;
        const maxAge = Math.max(
          0,
          ...noncompliant.flatMap((s) =>
            (s.vacancies || []).map((v) => v.age_days || 0),
          ),
        );
        const isRed = maxAge >= 7;
        const bg = isRed ? "#7F1D1D" : "#B45309";
        return (
          <Link
            to="/app/fsms-team"
            className="block mb-4 rounded-lg px-4 py-3"
            style={{ background: bg, color: "#fff" }}
            data-testid="dashboard-fsms-vacancy-banner"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <ShieldAlert size={18} className="flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">
                  {noncompliant.length === 1
                    ? `FSMS committee gap at ${noncompliant[0].site_name}`
                    : `FSMS committee gaps at ${noncompliant.length} sites`}
                  {isRed && <span className="ml-2 opacity-90">· {maxAge}d overdue</span>}
                </div>
                <div className="text-xs opacity-90 mt-0.5">
                  {noncompliant
                    .map((s) => `${s.site_name} (${s.vacancies.length})`)
                    .join(" · ")}
                  {" — appoint a holder to restore ISO 22000 compliance."}
                </div>
              </div>
              <Users2 size={14} className="opacity-90" />
              <span className="text-xs font-semibold underline">Review now</span>
            </div>
          </Link>
        );
      })()}

      {/* Food Prep disposal banner — overdue / expiring cooked items */}
      {foodPrep && (foodPrep.expired > 0 || foodPrep.expiring > 0) && (
        <Link
          to="/app/food-prep?tab=disposal"
          className="block mb-4 rounded-lg px-4 py-3"
          style={{ background: foodPrep.expired > 0 ? "#7F1D1D" : "#B45309", color: "#fff" }}
          data-testid="dashboard-food-prep-banner"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <AlertTriangle size={18} className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">
                Food Prep: {foodPrep.expired > 0 ? `${foodPrep.expired} item${foodPrep.expired === 1 ? "" : "s"} to dispose now` : "items expiring soon"}
                {foodPrep.expired > 0 && foodPrep.expiring > 0 && <span className="opacity-90"> · {foodPrep.expiring} expiring soon</span>}
              </div>
              <div className="text-xs opacity-90 mt-0.5">Review the disposal list to keep cooked-food records audit-ready.</div>
            </div>
            <span className="text-xs font-semibold underline">Open disposal list</span>
          </div>
        </Link>
      )}

      {showSiteOverview && siteCards && siteCards.length > 0 && (
        <div className="mb-8" data-testid="sites-overview">
          <div className="flex items-center justify-between mb-3">
            <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>Site overview</h2>
            <span className="opacity-60 text-sm">Click a card to drill into a single site</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {siteCards.map((s) => {
              const hasOpenDev = s.open_deviations > 0;
              const hasCalDue = s.calibration_due_30d > 0;
              const accent = hasOpenDev ? "var(--vb-error)" : hasCalDue ? "var(--vb-warning)" : "var(--vb-accent)";
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSite(s.id)}
                  className="vb-card p-5 text-left hover:shadow-lg transition-shadow"
                  data-testid={`site-overview-card-${s.id}`}
                  style={{ borderLeft: `4px solid ${accent}` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="vb-heading text-lg" style={{ color: "var(--vb-text-2)" }}>
                        <MapPin size={14} className="inline -mt-1 mr-1 opacity-70" />{s.name}
                      </div>
                      {s.location && <div className="text-xs opacity-60 mt-0.5">{s.location}</div>}
                    </div>
                    {hasOpenDev && <span className="vb-pill err text-[10px]">⚠ {s.open_deviations} open</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div className="flex items-center gap-2 opacity-80"><Wrench size={12} /> Equipment <strong className="ml-auto">{s.equipment_count}</strong></div>
                    <div className="flex items-center gap-2 opacity-80"><Building2 size={12} /> Facilities <strong className="ml-auto">{s.facility_count}</strong></div>
                    <div className="flex items-center gap-2 opacity-80"><UsersIcon size={12} /> Team <strong className="ml-auto">{s.team_count}</strong></div>
                    <div className={`flex items-center gap-2 ${hasCalDue ? "" : "opacity-80"}`} style={hasCalDue ? { color: "var(--vb-warning)" } : undefined}>
                      <Calendar size={12} /> Cal due ≤30d <strong className="ml-auto">{s.calibration_due_30d}</strong>
                    </div>
                    <div className={`col-span-2 flex items-center gap-2 ${hasOpenDev ? "" : "opacity-80"}`} style={hasOpenDev ? { color: "var(--vb-error)" } : undefined}>
                      <AlertTriangle size={12} /> Open deviations <strong className="ml-auto">{s.open_deviations}</strong> &nbsp;•&nbsp; <span className="opacity-70">Last 7d: {s.deviations_7d}</span>
                    </div>
                  </div>
                  <div className="mt-3 text-xs flex items-center gap-1" style={{ color: "var(--vb-text-2)" }}>
                    Open <ChevronRight size={12} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => {
          const inner = (
            <>
              <div className="flex items-center justify-between">
                <span className="label">{c.label}</span>
                <c.icon size={18} color={c.color} />
              </div>
              <span className="value" style={{ color: c.color }}>{c.value}</span>
              {c.sub && <div className="text-[11px] opacity-70 mt-1">{c.sub}</div>}
            </>
          );
          const testid = c.testid || `stat-${c.label.toLowerCase().replace(/[\s()]+/g, "-")}`;
          return c.to ? (
            <Link key={c.label} to={c.to} className="vb-stat hover:shadow-md transition-shadow"
              style={{ display: "block", color: "inherit", textDecoration: "none" }}
              data-testid={testid}>
              {inner}
            </Link>
          ) : (
            <div key={c.label} className="vb-stat" data-testid={testid}>
              {inner}
            </div>
          );
        })}
      </div>

      <h2 className="vb-heading text-xl mb-3" style={{ color: "var(--vb-text-2)" }}>Recent readings</h2>
      <div className="vb-card overflow-hidden">
        <table className="vb-table">
          <thead><tr><th>When</th><th>CCP</th><th>Value</th><th>Range</th><th>Logged by</th><th>Status</th></tr></thead>
          <tbody>
            {recent.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No readings yet — log your first one.</td></tr>}
            {recent.map((r) => (
              <tr key={r.id} data-testid={`recent-row-${r.id}`}>
                <td>{new Date(r.logged_at).toLocaleString()}</td>
                <td>{r.ccp_name}</td>
                <td><strong>{r.value}{r.unit}</strong></td>
                <td className="opacity-70">{r.min_value}–{r.max_value}{r.unit}</td>
                <td>{r.logged_by_name}</td>
                <td>{r.is_deviation ? <span className="vb-pill err">deviation</span> : <span className="vb-pill ok">in range</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
